'use client';

import React,{ useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { motion } from 'framer-motion';
import type { Materials, PriceTableProps, Material } from '../../types/materials';
import { materials } from '../../data/materials';

export default function PriceTable({ type, featured }: PriceTableProps) {
    const [loading, setLoading] = useState(true);
    const [highlightedId, setHighlightedId] = useState<string | null>(null);
    const searchParams = useSearchParams();
    
    let data: Materials = [];
    if (type === 'black') {
      data = materials.black;
    } else if (type === 'color') {
      data = materials.color;
    } else if (type === 'special') {
      data = materials.special;
    } else if (type === 'recyclable') {
      data = materials.recyclable;
    }

    useEffect(() => {
        setLoading(false);
    }, []);

    useEffect(() => {
        const highlight = searchParams.get('highlight');
        if (highlight) {
            setHighlightedId(highlight);
            
            // Ищем элемент на странице
            const element = document.getElementById(`metal-${highlight}`);
            
            if (element) {
                // Если элемент найден, прокручиваем к нему
                const yOffset = -100;
                const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                window.scrollTo({ top: y, behavior: 'smooth' });
            } else {
                // Если элемент не найден в текущей категории, проверяем все категории материалов
                const allMaterialTypes = ['black', 'color', 'special', 'recyclable'];
                let foundType = '';
                
                // Ищем, к какой категории принадлежит материал с данным ID
                for (const matType of allMaterialTypes) {
                    const materialList = materials[matType as keyof typeof materials];
                    if (materialList.some((mat: Material) => mat.id.toString() === highlight)) {
                        foundType = matType;
                        break;
                    }
                }
                
                // Если нашли категорию, к которой принадлежит материал, перенаправляем на соответствующую секцию
                if (foundType && foundType !== type) {
                    const sectionElement = document.querySelector(`[data-section-type="${foundType}"]`);
                    if (sectionElement) {
                        const yOffset = -100;
                        const y = sectionElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
                        window.scrollTo({ top: y, behavior: 'smooth' });
                        
                        // После прокрутки к секции, через небольшую задержку запускаем поиск элемента снова
                        setTimeout(() => {
                            const elementAfterScroll = document.getElementById(`metal-${highlight}`);
                            if (elementAfterScroll) {
                                const elemY = elementAfterScroll.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                window.scrollTo({ top: elemY, behavior: 'smooth' });
                            }
                        }, 500);
                    }
                }
            }
            
            // Убираем подсветку через 5 секунд с плавным затуханием
            setTimeout(() => {
                setHighlightedId(null);
            }, 5000);
        }
    }, [searchParams, type]);

    useEffect(() => {
        const fetchPrices = async () => {
            setLoading(true);
            try {
                let filteredPrices: { name: string; price: string }[] = [];
                
                if (type === 'black') {
                    filteredPrices = materials.black.map((item: Material) => ({
                        name: item.name,
                        price: item.price
                    }));
                } else if (type === 'color') {
                    filteredPrices = materials.color.map((item: Material) => ({
                        name: item.name,
                        price: item.price
                    }));
                } else if (type === 'special') {
                    filteredPrices = materials.special.map((item: Material) => ({
                        name: item.name,
                        price: item.price
                    }));
                } else if (type === 'recyclable') {
                    filteredPrices = materials.recyclable.map((item: Material) => ({
                        name: item.name,
                        price: item.price
                    }));
                }

                if (featured) {
                    filteredPrices = filteredPrices.slice(0, 5);
                }

                setPrices(filteredPrices);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching prices:', error);
                setLoading(false);
            }
        };
        fetchPrices();
    }, [type, featured]);

    if (loading) {
        return <div className="loading font-display">Загрузка цен...</div>;
    }

    return (
        <div className="h-full">
            <div className="relative h-full">
                <div className="grid grid-cols-1 gap-2">
                    {data.map((item) => (
                        <motion.div
                            key={item.id}
                            id={`metal-${item.id}`}
                            className={`flex items-center justify-between bg-white rounded-lg overflow-hidden transition-all duration-300 relative py-2 px-3
                                ${highlightedId === item.id.toString() ? 'highlight-row' : 'hover:bg-gray-50'}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ 
                                opacity: 1, 
                                y: 0
                            }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="flex flex-col">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-sm font-medium text-gray-900">
                                        {item.name}
                                    </h3>
                                </div>
                                {item.content && (
                                    <p className="text-xs text-gray-500">
                                        {item.content}
                                    </p>
                                )}
                            </div>
                            <div className="flex items-baseline gap-1 ml-4">
                                <span className="text-xs text-gray-500">від</span>
                                <span className="text-base font-semibold text-amber-600">{item.price.replace('від ', '')}</span>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function setPrices(filteredPrices: { name: string; price: string; }[]) {
    return filteredPrices;
}